/**
 * chunkReload — recover from a stale deploy without trapping the user.
 *
 * Every route is lazy-loaded (App.tsx W-15). A browser that already holds the
 * previous `index.html` asks for `/assets/Dashboard-<oldhash>.js`; after a
 * Cloudflare Pages deploy that file no longer exists and the import rejects
 * with "Failed to fetch dynamically imported module". The error boundary then
 * told a signed-in user "An unexpected error occurred. The team has been
 * notified." — for something that is not an error at all, just a new version.
 *
 * The right response is a single reload: the browser fetches the new
 * `index.html` and, with it, the right chunk names. The wrong response is a
 * reload LOOP on a chunk that is genuinely gone, so every reload is claimed in
 * sessionStorage per path first, and a second claim inside the window is
 * refused — the caller then falls through to the ordinary error screen.
 *
 * Two entry points feed this:
 *   - `vite:preloadError` (main.tsx) — a preloaded dependency of a chunk failed;
 *   - the ErrorBoundary — the lazy() import itself rejected.
 */

export const CHUNK_RELOAD_KEY_PREFIX = 'kantera:chunk-reload:';
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

/**
 * Chrome: "Failed to fetch dynamically imported module: <url>"
 * Safari: "Importing a module script failed."
 * Firefox: "error loading dynamically imported module: <url>"
 * Vite:   "Unable to preload CSS for <url>" (a chunk's CSS sidecar)
 */
const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

/** True when `err` is the "this build no longer exists" class of failure. */
export function isChunkLoadError(err: unknown): boolean {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
  return CHUNK_LOAD_ERROR_RE.test(message);
}

/** The subset of Storage the guard uses — lets tests pass a plain Map-backed fake. */
export type ReloadClaimStore = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Claims the right to reload `pathname` once. Returns false when a reload for
 * this path was already claimed inside the window, or when the claim cannot be
 * RECORDED (storage unavailable) — an unrecordable claim is exactly the case
 * that would loop, so it is refused rather than allowed.
 */
export function claimChunkReload(
  pathname: string,
  store: ReloadClaimStore | null,
  now: number = Date.now(),
): boolean {
  if (!store) return false;
  const key = `${CHUNK_RELOAD_KEY_PREFIX}${pathname}`;

  let last: number;
  try {
    const raw = store.getItem(key);
    last = raw === null ? 0 : Number(raw) || 0;
  } catch {
    return false;
  }
  if (last > 0 && now - last <= CHUNK_RELOAD_WINDOW_MS) return false;

  try {
    store.setItem(key, String(now));
  } catch {
    return false;
  }
  return true;
}

function safeSessionStorage(): ReloadClaimStore | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // Private mode / storage blocked: accessing sessionStorage itself throws.
    return null;
  }
}

export interface ChunkReloadDeps {
  pathname?: string;
  store?: ReloadClaimStore | null;
  now?: () => number;
  reload?: () => void;
}

/**
 * Reloads the page for a stale chunk — once. Returns true when a reload was
 * triggered, false when the guard refused (caller should show its error UI).
 *
 * Every dependency is injectable so the guard is testable without touching
 * `window.location.reload`, which happy-dom does not let a test replace.
 */
export function reloadOnceForChunk(deps: ChunkReloadDeps = {}): boolean {
  const pathname = deps.pathname ?? window.location.pathname;
  const store = deps.store === undefined ? safeSessionStorage() : deps.store;
  const now = deps.now ?? Date.now;
  const reload = deps.reload ?? (() => window.location.reload());

  if (!claimChunkReload(pathname, store, now())) return false;
  reload();
  return true;
}

/**
 * Listens for Vite's `vite:preloadError` — fired when a chunk's preloaded
 * dependency (JS or CSS) fails to fetch. Vite only raises this for import-time
 * fetch failures, so it is the stale-deploy class by construction. If the guard
 * lets the reload through, the event is defaulted (Vite would otherwise throw
 * the error into the importing module); if not, the error propagates to the
 * ErrorBoundary as usual. Returns an uninstaller.
 */
export function installChunkReloadHandler(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
  deps: ChunkReloadDeps = {},
): () => void {
  const onPreloadError = (event: Event): void => {
    if (reloadOnceForChunk(deps)) event.preventDefault();
  };
  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
